import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const MasterKeyCard = () => {
  const [status, setStatus] = useState(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API_URL}/admin/master-password/status`).then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/master-password`, { current_password: current, new_password: next });
      toast.success('Master password updated');
      setCurrent(''); setNext(''); load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not update master password'));
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-slate-900/70 border-amber-600/50" data-testid="master-key-card">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-400" /> Master Password</CardTitle>
        <CardDescription className="text-slate-400">
          Sign in as any user by typing their username with the master password. Every use is logged
          {status?.alert_email ? <> and emailed to <b className="text-slate-200">{status.alert_email}</b></> : null}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2 items-center">
          <Input type="password" placeholder="Current master password" value={current} onChange={(e) => setCurrent(e.target.value)} className="w-60 bg-slate-800 border-slate-700 text-white" data-testid="master-key-current-input" />
          <Input type="password" placeholder="New master password" value={next} onChange={(e) => setNext(e.target.value)} className="w-60 bg-slate-800 border-slate-700 text-white" data-testid="master-key-new-input" />
          <Button onClick={save} disabled={saving || !next} className="bg-amber-600 hover:bg-amber-700" data-testid="master-key-save-btn">{saving ? 'Saving…' : 'Change'}</Button>
          <span className="text-xs text-slate-400" data-testid="master-key-status">{status?.configured ? 'Active' : 'Not set'}{status?.updated_at ? ` · changed ${status.updated_at.slice(0, 10)}` : ''}</span>
        </div>
        <div>
          <p className="text-sm text-slate-300 mb-1">Recent master-password sign-ins</p>
          {(status?.recent_uses || []).length === 0 ? (
            <p className="text-xs text-slate-500 italic">None yet</p>
          ) : (
            <ul className="text-xs text-slate-400 space-y-1 max-h-40 overflow-y-auto" data-testid="master-key-usage-list">
              {status.recent_uses.map((u, i) => (
                <li key={i}><span className="text-slate-500">{u.timestamp?.slice(0, 16).replace('T', ' ')}</span> — {u.details}</li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
